import { COMPETITION_PRIZE_TYPES } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import React from 'react';

import { OfferPromotionCard } from '@components/offer/promotion-card';
import { Box, Flex } from '../../grid';
import { H3, P } from '../../typography';
import { Image } from '../../image';
import { PRIZE_COPY, PRIZE_DISCLAIMER } from './constants';

const CompetitionPrize = ({ hasConsentToEmailGuest, prize, profile }) => {
  const { OfferStore } = useRootStore();

  const guestPrizeCopy = {
    [COMPETITION_PRIZE_TYPES.OFFER]: hasConsentToEmailGuest ? PRIZE_COPY.SENT_VIA_EMAIL : PRIZE_COPY.USE_CODE_NOW,
    [COMPETITION_PRIZE_TYPES.GIFT_CARD]: hasConsentToEmailGuest ? PRIZE_COPY.SENT_VIA_EMAIL : PRIZE_COPY.GIFT_CARD_WON,
  };

  const offer = prize.offer ? OfferStore.getOfferByHash(prize.offer.hash) : null;

  const prizeContent = {
    [COMPETITION_PRIZE_TYPES.OFFER]: (
      <Box width="380px" maxWidth="100%" mx="auto">
        <H3 textAlign="center" mt={2} mb="44px" mx="auto">
          Congratulations
        </H3>
        <Flex mb={2} justifyContent="center">
          {offer ? <OfferPromotionCard offer={offer} hideButton /> : null}
        </Flex>
        <Box mx="auto" width="100%" mb="34px">
          <P variant={2} mb={1} fontWeight="bold" textAlign="center">
            {profile ? PRIZE_COPY.FOUND_IN_OFFERS : guestPrizeCopy[COMPETITION_PRIZE_TYPES.OFFER]}
          </P>
          {!profile && !hasConsentToEmailGuest && prize?.offer?.hash && (
            <Flex justifyContent="center" mb={1}>
              <Box p={1} backgroundColor="#F1FEFC" fontWeight="bold">
                {prize?.offer?.hash}
              </Box>
            </Flex>
          )}
          {!profile && (
            <P color="grey500" variant={4} textAlign="center">
              {hasConsentToEmailGuest ? PRIZE_DISCLAIMER.ALLOW_48_HOURS_EMAIL : PRIZE_DISCLAIMER.WRITE_DOWN_DETAILS}
            </P>
          )}
        </Box>
      </Box>
    ),
    [COMPETITION_PRIZE_TYPES.GIFT_CARD]: (
      <>
        <H3 textAlign="center" mt={2} mb="1.5rem">
          Congratulations
        </H3>
        <Box display="flex" justifyContent="center" width="100%" mb="1.5rem">
          <Image alt="a Nando’s gift card" src="/static/images/spin-to-win/gift-card.png" width="295" height="183" />
        </Box>
        <Box width="380px" maxWidth="100%" mx="auto" mb="20px">
          <P variant={2} fontWeight="bold" textAlign="center" mb={1}>
            {profile
              ? `Your $50 e-gift card will be sent to you via email at ${profile.email}`
              : guestPrizeCopy[COMPETITION_PRIZE_TYPES.GIFT_CARD]}
          </P>
          {!profile && !hasConsentToEmailGuest && (
            <Flex flexDirection="column" alignItems="center" mb={1}>
              <Flex flexDirection="column" alignItems="center" mb="0.5rem">
                <Box mb="0.5rem">Card number</Box>
                <Box p={1} backgroundColor="#F1FEFC" fontWeight="bold">
                  {prize?.giftCard?.cardNumber}
                </Box>
              </Flex>
              <Flex flexDirection="column" alignItems="center">
                <Box mb="0.5rem">Verification code</Box>
                <Box p={1} backgroundColor="#F1FEFC" fontWeight="bold">
                  {prize?.giftCard?.verificationCode}
                </Box>
              </Flex>
            </Flex>
          )}
          <P color="grey500" variant={4} textAlign="center">
            {profile || (!profile && hasConsentToEmailGuest)
              ? PRIZE_DISCLAIMER.ALLOW_48_HOURS_EMAIL
              : PRIZE_DISCLAIMER.WRITE_DOWN_DETAILS}
          </P>
        </Box>
      </>
    ),
    [COMPETITION_PRIZE_TYPES.GRAND_PRIZE]: (
      <>
        <H3 textAlign="center" mt={2} mb="1rem">
          Congratulations
        </H3>
        <Box display="flex" justifyContent="center" width="100%" mb="1rem">
          <Image
            alt="a group of people enjoying a meal at a Nando’s restaurant"
            src="/static/images/spin-to-win/grand-prize.png"
            width="400"
            height="200"
          />
        </Box>
        <Box width="75%" pb="34px">
          <Box display="flex" flex={1} flexDirection="row">
            <Box flexShrink={1}>
              <P variant={3} textAlign="center" fontWeight="bold" mb={1}>
                {profile
                  ? `Congratulations on winning a major prize. Invite your friends & family to a feast, on us. More
                  information will be sent to you via your email at ${profile?.email}`
                  : PRIZE_COPY.GUEST_GRAND_PRIZE}
              </P>
              {profile && (
                <P color="grey500" variant={4} textAlign="center">
                  Please allow up to 48 hours for the email to arrive.
                </P>
              )}
            </Box>
          </Box>
        </Box>
      </>
    ),
  }[prize.prizeType];

  if (!prizeContent) {
    throw Error(`Unknown prize type: ${prize.type}`);
  }

  return prizeContent;
};

const ComposedCompetitionPrize = observer(CompetitionPrize);

export { ComposedCompetitionPrize as CompetitionPrize };
